import classnames from 'classnames'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { Icon } from 'semantic-ui-react'

import { isBot, isBotSoccerCup } from 'app'
import { trackEvent } from 'global-utils/Analytics'
import { getImageURL, IMAGE_SIZES } from 'images'
import { Drawer } from 'ui'

import { authAction, authLogout } from 'actions/AuthActions'

import NewVersionCheck from './NewVersionCheck'

import type { FC } from 'react'

export const MINIMUM_FIXED_WIDTH = 768

type UserMenuProps = {
  callbackToggleOpen?: (toggleOpen: () => void) => void
  className?: string
}

const UserMenu: FC<UserMenuProps> = ({ callbackToggleOpen, className }) => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  // auth type
  // @ts-expect-error usr not defined
  const authUser = useSelector((state: GlobalState) => state.auth?.usr)
  // @ts-expect-error width not defined
  const stageWidth = useSelector((state: GlobalState) => state.environment?.stage?.width || 0)
  const unseen = useSelector((state: GlobalState) => state.userNotifications.unseen)

  const [isOpen, setIsOpen] = useState(stageWidth >= MINIMUM_FIXED_WIDTH)

  useEffect(() => {
    if (callbackToggleOpen) {
      callbackToggleOpen(() => setIsOpen((isOpenNow) => !isOpenNow))
    }
  }, [callbackToggleOpen])

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const menuOptionClassName =
    'flex items-center gap-[0.5em] p-[0.5em] !text-navy border-0 border-solid border-b border-white hover:border-greyLightMed'
  const selectedClassName = '!text-green border-green'
  // @ts-expect-error total not defined
  const total = unseen.pagination?.total || 0
  const imageURL = authUser.avatar ? getImageURL(authUser.avatar, IMAGE_SIZES.FIT_100_100) : undefined

  return (
    <Drawer
      className={classnames('Site__container__drawer', className)}
      display={stageWidth > MINIMUM_FIXED_WIDTH ? 'static' : 'fixed'}
      footer={
        <div className="text-[0.8em] text-grey">
          <a href="mailto:info@sweatbits.co" onClick={() => trackEvent('User', 'Click', 'info@sweatbits.co')}>
            info@sweatbits.co
          </a>
          <NewVersionCheck className="text-sm block" />
        </div>
      }
      header={
        <Link className={menuOptionClassName} onClick={() => trackEvent('LeftMenu', 'Click', 'Avatar')} to="/settings">
          <div className="shrink-0 text-greyDark size-12 border-2 border-greyLightest bg-greyLightest rounded-full">
            <div className="size-full border border-white bg-greyLightest rounded-full overflow-hidden shadow">
              {imageURL ? (
                <img alt="avatar" className="size-full" src={imageURL} />
              ) : (
                <svg className="size-full" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8 0-1.168.258-2.275.709-3.276.154.09.308.182.456.276.396.25.791.5 1.286.688.494.187 1.088.312 1.879.312.792 0 1.386-.125 1.881-.313s.891-.437 1.287-.687.792-.5 1.287-.688c.494-.187 1.088-.312 1.88-.312s1.386.125 1.88.313c.495.187.891.437 1.287.687s.792.5 1.287.688c.178.067.374.122.581.171.191.682.3 1.398.3 2.141 0 4.411-3.589 8-8 8z" />
                  <circle cx="8.5" cy="12.5" r="1.5" />
                  <circle cx="15.5" cy="12.5" r="1.5" />
                </svg>
              )}
            </div>
          </div>
          <div className="grow line-clamp-3">{authUser.name}</div>
        </Link>
      }
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Link
        className={classnames(menuOptionClassName, pathname === '/' && selectedClassName)}
        onClick={() => {
          trackEvent('LeftMenu', 'Click', 'home')
          document.location = '/'
        }}
        to="/"
      >
        <Icon name="home" /> Inicio
      </Link>
      <Link
        className={classnames(menuOptionClassName, pathname === `/user/${authUser.id}` && selectedClassName)}
        onClick={() => trackEvent('LeftMenu', 'Click', 'profile')}
        to={`/user/${authUser.id}`}
      >
        <Icon name="user" /> Mi perfil
      </Link>
      <Link
        className={classnames(menuOptionClassName, pathname === '/settings' && selectedClassName)}
        onClick={() => trackEvent('LeftMenu', 'Click', 'settings')}
        to="/settings"
      >
        <Icon name="settings" /> Mi cuenta
      </Link>
      <div className="my-2 border-0 border-t border-solid border-greyLightest" />
      <Link
        className={classnames(menuOptionClassName, pathname === '/tournaments' && selectedClassName)}
        onClick={() => trackEvent('LeftMenu', 'Click', 'tournaments')}
        to="/tournaments_v2/user"
      >
        <Icon name="trophy" /> Mis Torneos
      </Link>
      <Link
        className={classnames(menuOptionClassName, pathname === '/teams' && selectedClassName)}
        onClick={() => trackEvent('LeftMenu', 'Click', 'teams')}
        to="/teams"
      >
        <Icon name="group" /> Mis Equipos
      </Link>
      <Link
        className={classnames(menuOptionClassName, pathname === '/institutions' && selectedClassName)}
        onClick={() => trackEvent('LeftMenu', 'Click', 'institutions')}
        to="/institutions"
      >
        <Icon name="building outline" /> Mis Instituciones
      </Link>
      <div className="my-2 border-0 border-t border-solid border-greyLightest" />
      <Link
        className={classnames(
          menuOptionClassName,
          'flex justify-between',
          pathname === '/notifications' && selectedClassName,
        )}
        onClick={() => trackEvent('LeftMenu', 'Click', 'notifications')}
        to="/notifications"
      >
        <span>
          <Icon name="mail outline" /> Notificaciones
        </span>
        {!!total && <div className="ui mini red label !rounded-full">{total}</div>}
      </Link>
      <Link
        className={classnames(menuOptionClassName, 'mt-[3em]')}
        onClick={() => {
          trackEvent('LeftMenu', 'Click', 'logout')
          dispatch(authAction(null))
          dispatch(authLogout())
        }}
        to="/"
      >
        <Icon name="sign out" /> Salir
      </Link>
      {(isBot(authUser.email) || isBotSoccerCup(authUser.email)) && (
        <>
          <Link
            className={classnames(
              menuOptionClassName,
              pathname === '/sweatbits/admin' && selectedClassName,
              'mt-[1em] bg-greyLight',
            )}
            onClick={() => trackEvent('LeftMenu', 'Click', 'admin')}
            to="/sweatbits/admin"
          >
            <Icon name="wizard" />
            Admin
          </Link>
          <Link
            className={classnames(
              menuOptionClassName,
              pathname === '/stats4nerds' && selectedClassName,
              'mt-[0.5em] !bg-greyLight',
            )}
            onClick={() => trackEvent('LeftMenu', 'Click', 'admin')}
            to="/stats4nerds"
          >
            <Icon name="area graph" />
            Stats 4 Nerds
          </Link>
        </>
      )}
    </Drawer>
  )
}

export default UserMenu
